import { createResourceHooks, createSupabaseChannelSubscribe } from '@/lib/query/resourceFactory';
import { fetchPublicPropertyList } from '@/lib/services/publicProperties';
import { supabase } from '@/lib/supabase/client';
import { getPublicProperty } from '@/lib/supabase/publicProperties';
import type {
  PublicPropertyDetail,
  PublicPropertyFilterState,
  PublicPropertyListResult,
} from '@/lib/types/publicProperty';
import type { QueryKey } from '@tanstack/react-query';

export interface PublicPropertyListParams {
  page?: number;
  pageSize?: number;
  filters?: PublicPropertyFilterState;
  vacancyOnly?: boolean;
}

const scope = 'public-properties';
const listBaseKey: QueryKey = [scope, 'list'];

const subscribeToPublishedUnits = createSupabaseChannelSubscribe<PublicPropertyListParams>({
  supabase,
  channel: 'public-properties-vacancy',
  table: 'units',
  event: '*',
  mapQueryKeys: () => [listBaseKey],
});

export const publicPropertyResource = createResourceHooks<
  PublicPropertyListParams,
  PublicPropertyListResult,
  { identifier: string },
  PublicPropertyDetail,
  Record<string, never>
>({
  scope,
  defaults: {
    staleTime: 60 * 1000,
  },
  list: {
    key: (params) => ({
      page: params.page,
      pageSize: params.pageSize,
      filters: params.filters,
      vacancyOnly: params.vacancyOnly ?? false,
    }),
    fetch: ({ page, pageSize, filters, vacancyOnly, signal }) =>
      fetchPublicPropertyList({ page, pageSize, filters, vacancyOnly, signal }),
    subscribe: ({ params, queryClient }) => subscribeToPublishedUnits({ params, queryClient }),
  },
  detail: {
    key: (params) => params.identifier,
    fetch: ({ identifier, signal }) => getPublicProperty({ identifier, signal }),
  },
});
